import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import products from '../data/products';
import RequestQuotationModal from '../components/RequestQuotationModal';

const ProductDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Find the product from the products array based on id
  React.useEffect(() => {
    const product = products.find(p => p.id === id);
    setSelectedProduct(product);
    // Scroll to top when product changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const openQuotationModal = () => {
    setIsQuotationModalOpen(true);
  };

  const closeQuotationModal = () => {
    setIsQuotationModalOpen(false);
  };

  // Get related products from the same category
  const getRelatedProducts = () => {
    if (!selectedProduct) return [];
    return products
      .filter(product =>
        product.category === selectedProduct.category &&
        product.id !== selectedProduct.id
      )
      .slice(0, 4);
  };

  // Get category-specific certificates
  const getCertificatesForCategory = (category) => {
    const certificatesByCategory = {
      'cocopeat_coir_products': [
        "images.jpg",
        "UKcert.png",
        "makeInIndia.webp",
        "OrganicCert.webp",
        "labTested.jpg",
        "CoirBoard.png"
        // "CoirBoard.png",
        // "BPI.JPG",
        // "sgs.png",
        // "TUV.webp",
        // "what-is-iso-9001-compliance.webp"
      ],
      'organic_fertilizers': [
        "Greencert-logo.png",
       "makeInIndia.webp",
        "OrganicCert.webp",
        "labTested.jpg",
        "Apeda.png",
        
      ],
      'herbal_products': [
        "OrganicCert.webp",
        "Greencert-logo.png",
        "fda-certification-consultant-500x500.webp",
        "what-is-iso-9001-compliance.webp",
        "sgs.png",
        "Apeda.png"
      ],
      'bagasse_tableware': [
        "BPI.JPG",
        "BRCGS-LOGO.jpg",
        "fda-certification-consultant-500x500.webp",
        "haccp-certification-services-500x500.webp",
        "what-is-iso-9001-compliance.webp",
        "sgs.png",
         "labTested.jpg",
        "TUV.webp"
      ]
    };

    // Return category-specific certificates or all certificates as fallback
    return certificatesByCategory[category] || [
      "Apeda.png", "BPI.JPG", "BRCGS-LOGO.jpg", "CoirBoard.png",
      "fda-certification-consultant-500x500.webp", "Greencert-logo.png",
      "haccp-certification-services-500x500.webp", "OrganicCert.webp",
      "sgs.png", "TUV.webp", "UKcert.png", "what-is-iso-9001-compliance.webp"
    ];
  };

  if (!selectedProduct) {
    return (
      <div className="min-h-screen bg-[#FFF3D6] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#8F5D3F] mx-auto mb-4"></div>
          <p className="text-[#8F5D3F] text-lg">Loading product details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF3D6]">
      {/* Add proper spacing from header */}
      <div className="pt-20"></div>

      <div className="max-w-7xl mx-auto py-8 md:py-12 px-4 sm:px-6 lg:px-8">
        {/* Main Product Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
          {/* Left Column - Product Image and Additional Info */}
          <div className="flex flex-col space-y-6 md:space-y-8">
            {/* Product Image */}
            <div className="flex justify-center items-start">
              <div className="w-full max-w-lg aspect-square relative">
                <div className="relative w-full h-full bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-4 md:p-8">
                  {selectedProduct.images && selectedProduct.images.length > 1 ? (
                    <>
                      {/* Carousel for multiple images */}
                      <img
                        src={selectedProduct.images[currentImageIndex]}
                        alt={`${selectedProduct.name} - Image ${currentImageIndex + 1}`}
                        className="w-full h-full object-contain rounded-lg"
                      />

                      {/* Previous Button */}
                      <button
                        onClick={() => setCurrentImageIndex((prev) =>
                          prev === 0 ? selectedProduct.images.length - 1 : prev - 1
                        )}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#8b5e3c] p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      {/* Next Button */}
                      <button
                        onClick={() => setCurrentImageIndex((prev) =>
                          prev === selectedProduct.images.length - 1 ? 0 : prev + 1
                        )}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#8b5e3c] p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>

                      {/* Image Dots Indicator */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {selectedProduct.images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${
                              currentImageIndex === index
                                ? 'bg-[#8b5e3c] w-6'
                                : 'bg-gray-300 hover:bg-gray-400'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Single Image */}
                      <img
                        src={selectedProduct.image}
                        alt={selectedProduct.name}
                        className="w-full h-full object-contain rounded-lg"
                      />
                    </>
                  )}

                  {/* Floating badge */}
                  <div className="absolute top-4 left-4 bg-[#8b5e3c] text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                    Premium Quality
                  </div>
                </div>

                {/* Thumbnail Strip for Carousel */}
                {selectedProduct.images && selectedProduct.images.length > 1 && (
                  <div className="flex gap-2 mt-4 justify-center">
                    {selectedProduct.images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                          currentImageIndex === index
                            ? 'border-[#8b5e3c] scale-110 shadow-lg'
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-contain bg-white p-1"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Product Specifications - Moved to left column */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-[#2c541d] mb-4 flex items-center">
                <span className="w-2 h-6 bg-[#8b5e3c] rounded-full mr-3"></span>
                Product Specifications
              </h3>
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full">
                  <tbody>
                    <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-4 bg-gray-50 font-semibold text-[#2c541d] text-sm md:text-base">Product</td>
                      <td className="px-4 py-4 bg-white text-gray-700 text-sm md:text-base">{selectedProduct.name}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-4 bg-gray-50 font-semibold text-[#2c541d] text-sm md:text-base">HSN Code</td>
                      <td className="px-4 py-4 bg-white text-gray-700 text-sm md:text-base">{selectedProduct.hsCode}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-4 bg-gray-50 font-semibold text-[#2c541d] text-sm md:text-base">Category</td>
                      <td className="px-4 py-4 bg-white text-gray-700 text-sm md:text-base capitalize">
                        {selectedProduct.category.replace('_', ' ').toLowerCase()}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-4 bg-gray-50 font-semibold text-[#2c541d] text-sm md:text-base">Country/Region</td>
                      <td className="px-4 py-4 bg-white text-gray-700 text-sm md:text-base">India</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-4 bg-gray-50 font-semibold text-[#2c541d] text-sm md:text-base">Packaging Type</td>
                      <td className="px-4 py-4 bg-white text-gray-700 text-sm md:text-base">Professional Packaging</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-4 bg-gray-50 font-semibold text-[#2c541d] text-sm md:text-base">Quality Grade</td>
                      <td className="px-4 py-4 bg-white text-gray-700 text-sm md:text-base">Premium Grade</td>
                    </tr>
                    {selectedProduct.category === 'organic_fertilizers' && (
                      <tr className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-4 py-4 bg-gray-50 font-semibold text-[#2c541d] text-sm md:text-base">Shelf Life</td>
                        <td className="px-4 py-4 bg-white text-gray-700 text-sm md:text-base">1 Year</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Column - Product Info Section */}
          <div className="flex flex-col space-y-6 md:space-y-8">
            {/* Header Section */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#2c541d] mb-3 leading-tight">
                {selectedProduct.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-500 text-lg md:text-xl">
                  {'★★★★★'.split('').map((star, i) => (
                    <span key={i}>{star}</span>
                  ))}
                </div>
                <span className="ml-2 text-gray-600 text-sm md:text-base">(5.0 rating)</span>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  onClick={openQuotationModal}
                  className="bg-[#8b5e3c] hover:bg-[#8b5e3c] text-white px-8 py-3 rounded-lg font-semibold text-base transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex-1"
                >
                  Request Quotation
                </Button>
                {/* <Button
                  className="bg-white border-2 border-[#8b5e3c] text-[#8b5e3c] hover:bg-[#8b5e3c] hover:text-white px-8 py-3 rounded-lg font-semibold text-base transition-all duration-300 transform hover:scale-105 flex-1"
                >
                  Download Brochure
                </Button> */}
              </div>
            </div>

            {/* Product Description */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-[#2c541d] mb-4 flex items-center">
                <span className="w-2 h-6 bg-[#8b5e3c] rounded-full mr-3"></span>
                Product Description
              </h3>
              <p className="text-gray-700 leading-relaxed text-base md:text-lg">
                {selectedProduct.description}
              </p>
            </div>

            {/* Applications Section */}
            {selectedProduct.applications && selectedProduct.applications.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-[#2c541d] mb-4 flex items-center">
                  <span className="w-2 h-6 bg-[#8b5e3c] rounded-full mr-3"></span>
                  Applications
                </h3>
                <div className="space-y-3">
                  {selectedProduct.applications.map((application, index) => (
                    <div
                      key={index}
                      className="flex items-start p-3 bg-[#fff3d6] rounded-lg hover:bg-blue-50 transition-colors duration-200"
                    >
                      <div className="w-6 h-6 bg-[#8b5e3c] rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <span className="text-white font-bold text-xs">✓</span>
                      </div>
                      <span className="text-gray-700 font-medium text-sm leading-relaxed">{application}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Types Section */}
            {selectedProduct.types && selectedProduct.types.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-[#2c541d] mb-4 flex items-center">
                  <span className="w-2 h-6 bg-[#8b5e3c] rounded-full mr-3"></span>
                  Types Available
                </h3>
                <div className="space-y-3">
                  {selectedProduct.types.map((type, index) => (
                    <div
                      key={index}
                      className="flex items-start p-3 bg-[#fff3d6] rounded-lg hover:bg-blue-50 transition-colors duration-200"
                    >
                      <div className="w-6 h-6 bg-[#8b5e3c] rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <span className="text-white font-bold text-xs">•</span>
                      </div>
                      <span className="text-gray-700 font-medium text-sm leading-relaxed">{type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How to Use Section */}
            {selectedProduct.howToUse && selectedProduct.howToUse.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-[#2c541d] mb-4 flex items-center">
                  <span className="w-2 h-6 bg-[#8b5e3c] rounded-full mr-3"></span>
                  How to Use
                </h3>
                <div className="space-y-3">
                  {selectedProduct.howToUse.map((step, index) => (
                    <div
                      key={index}
                      className="flex items-start p-3 bg-[#fff3d6] rounded-lg hover:bg-green-50 transition-colors duration-200"
                    >
                      <div className="w-7 h-7 bg-[#8b5e3c] rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-white font-bold text-xs">{index + 1}</span>
                      </div>
                      <span className="text-gray-700 font-medium text-sm leading-relaxed">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mix Combinations Section */}
            {selectedProduct.mixCombinations && selectedProduct.mixCombinations.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-[#2c541d] mb-4 flex items-center">
                  <span className="w-2 h-6 bg-[#8b5e3c] rounded-full mr-3"></span>
                  Special Cocopeat Mix Combinations
                </h3>
                <div className="space-y-3">
                  {selectedProduct.mixCombinations.map((mix, index) => (
                    <div
                      key={index}
                      className="flex items-start p-3 bg-[#fff3d6] rounded-lg hover:bg-purple-50 transition-colors duration-200"
                    >
                      <div className="w-6 h-6 bg-[#8b5e3c] rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <span className="text-white font-bold text-xs">•</span>
                      </div>
                      <span className="text-gray-700 font-medium text-sm leading-relaxed">{mix}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Special Mixers Section */}
            {selectedProduct.specialMixers && selectedProduct.specialMixers.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-[#2c541d] mb-4 flex items-center">
                  <span className="w-2 h-6 bg-[#8b5e3c] rounded-full mr-3"></span>
                  Cocopeat Special Mixers (5Kg Blocks)
                </h3>
                <div className="space-y-3">
                  {selectedProduct.specialMixers.map((mixer, index) => (
                    <div
                      key={index}
                      className="flex items-start p-3 bg-[#fff3d6] rounded-lg hover:bg-orange-50 transition-colors duration-200"
                    >
                      <div className="w-6 h-6 bg-[#8b5e3c] rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <span className="text-white font-bold text-xs">•</span>
                      </div>
                      <span className="text-gray-700 font-medium text-sm leading-relaxed">{mixer}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Performance Section */}
            {selectedProduct.performance && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-[#2c541d] mb-4 flex items-center">
                  <span className="w-2 h-6 bg-[#8b5e3c] rounded-full mr-3"></span>
                  Performance & Yield
                </h3>
                <p className="text-gray-700 leading-relaxed text-base">
                  {selectedProduct.performance}
                </p>
              </div>
            )}

            {/* Features Section */}
            {selectedProduct.features && selectedProduct.features.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-[#2c541d] mb-4 flex items-center">
                  <span className="w-2 h-6 bg-[#8b5e3c] rounded-full mr-3"></span>
                  Key Features
                </h3>
                <div className="space-y-3">
                  {selectedProduct.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-start p-3 bg-[#fff3d6] rounded-lg hover:bg-green-50 transition-colors duration-200"
                    >
                      <div className="w-6 h-6 bg-[#8b5e3c] rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <span className="text-white font-bold text-xs">✓</span>
                      </div>
                      <span className="text-gray-700 font-medium text-sm leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Certifications Section */}
            {selectedProduct.certifications && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-[#2c541d] mb-4 flex items-center">
                  <span className="w-2 h-6 bg-[#8b5e3c] rounded-full mr-3"></span>
                  Certifications
                </h3>
                <p className="text-gray-700 leading-relaxed text-base font-medium">
                  {selectedProduct.certifications}
                </p>
              </div>
            )}

            {/* Benefits Section */}
            {selectedProduct.benefits && selectedProduct.benefits.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-[#2c541d] mb-4 flex items-center">
                  <span className="w-2 h-6 bg-[#8b5e3c] rounded-full mr-3"></span>
                  Key Benefits
                </h3>
                <div className="space-y-3">
                  {selectedProduct.benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex items-start p-3 bg-[#fff3d6] rounded-lg hover:bg-green-100 transition-colors duration-200"
                    >
                      <div className="w-6 h-6 bg-[#8b5e3c] rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <span className="text-white font-bold text-xs">✓</span>
                      </div>
                      <span className="text-gray-700 font-medium text-sm leading-relaxed">{benefit}</span>
                    </div>
                  ))}
                </div>
                {selectedProduct.result && (
                  <div className="mt-4 p-4 bg-green-50 border-l-4 border-[#8b5e3c] rounded-lg">
                    <p className="text-gray-800 font-semibold text-sm">
                      <span className="text-[#2c541d]">Result: </span>
                      {selectedProduct.result}
                    </p>
                  </div>
                )}
                {selectedProduct.suitableFor && (
                  <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
                    <p className="text-gray-800 text-sm">
                      <span className="font-semibold text-[#2c541d]">Suitable for: </span>
                      {selectedProduct.suitableFor}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Related Products Section */}
        <section className="w-full py-12 px-4 md:px-8 bg-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-[#2c541d] mb-3">Related Products</h2>
              <p className="text-gray-600 text-base md:text-lg">Discover more products from the same category</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {getRelatedProducts().length > 0 ? (
                getRelatedProducts().map((product) => (
                  <div 
                    key={product.id} 
                    className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden group cursor-pointer"
                    onClick={() => {
                      navigate(`/products/${product.id}`);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    <div className="aspect-square relative bg-white p-6">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4 text-center">
                      <h3 className="text-lg font-semibold text-[#2c541d] mb-3 line-clamp-2 min-h-[3rem] flex items-center justify-center">
                        {product.name}
                      </h3>
                      <Button
                        className="bg-[#8b5e3c] hover:bg-[#8b5e3c] text-white w-full py-2.5 rounded-lg font-medium transition-all duration-300 transform group-hover:scale-105"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/products/${product.id}`);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center text-gray-500 py-12 bg-white rounded-2xl shadow-lg">
                  <div className="text-6xl mb-4">📦</div>
                  <p className="text-lg">No related products found in this category.</p>
                </div>
              )}
            </div>
          </div>
        </section>

{/* Certificates and Compliance Section */}
<section className="w-full py-12 px-4 md:px-8 bg-transparent">
  <div className="max-w-7xl mx-auto">
    <div className="text-center mb-10">
      <h2 className="text-2xl md:text-3xl font-bold text-[#2c541d] mb-3">Our Certifications & Compliance</h2>
      <p className="text-gray-600 text-base md:text-lg">Quality assurance through recognized certifications</p>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
      {getCertificatesForCategory(selectedProduct.category).map((logo, index) => (
        <div
          key={index}
          className="flex justify-center items-center bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105 border border-gray-100"
        >
          <img
            src={require(`../assets/logos/${logo}`)}
            alt={`Certification ${index + 1}`}
            className="max-w-full max-h-12 md:max-h-16 object-contain hover:grayscale transition-all duration-300"
          />
        </div>
      ))}
    </div>
  </div>
</section>
      </div>

      {/* Request Quotation Modal */}
      <RequestQuotationModal
        isOpen={isQuotationModalOpen}
        onClose={closeQuotationModal}
        productName={selectedProduct?.name || ""}
      />
    </div>
  );
};

export default ProductDetails;